export type FunnelPrice = { priceId: string; amount: number };
export type FunnelPrices = {
  weekly?: FunnelPrice;
  annual?: FunnelPrice;
  /**
   * The undiscounted annual price, charged once the countdown offer has run out.
   * The countdown only appears when this exists and is dearer than `annual` —
   * a clock ticking down to no change in price is theatre, and one that strikes
   * through a price nobody is ever charged is a false reference price.
   */
  annualFull?: FunnelPrice;
  /** Middle tier — three plans catch intents that two collapse together. */
  quarterly?: FunnelPrice;
};

type StripePriceRow = {
  price: string;
  amount: number;
  interval: string | null;
  name?: string;
};

/**
 * The Stripe account is shared with other products (Shorty, Dro, Hardmax…), and
 * /get-products returns every active recurring price on the account. Picking by
 * interval alone — `find(p => p.interval === 'year')` — returns whichever price
 * Stripe listed first, which is the most recently created one. A new weekly or
 * yearly price on a sibling product would therefore silently take over this
 * funnel and charge our visitors someone else's plan (the account currently has
 * yearly prices up to $720). Our own product also has more than one active
 * weekly price, so interval alone is ambiguous even within it.
 *
 * So: pin the exact price ids, and if one is ever rotated in Stripe, fall back
 * only to prices belonging to our product — never to the whole account.
 */
const TOOLS_PRODUCT_NAME = "Tools for Procreate";
const WEEKLY_PRICE_ID = "price_1TnZcFE0awkWkU8cJLAcSbBB";
const ANNUAL_PRICE_ID = "price_1TjMnHE0awkWkU8cBNDufRWy";
// The list annual — $49.99 — struck through while the countdown runs and
// genuinely charged once it has stopped. `ANNUAL_PRICE_ID` above ($29.99) is
// what the coupon grants in the meantime.
//
// $49.99/year, verified against the live /get-products on 4 Aug. Together with
// the $29.99 above this makes the countdown worth exactly 40% — a figure the
// funnel derives rather than repeats, so it cannot drift from what Stripe bills.
//
// If this is ever emptied or the price archived, the countdown offer stops
// rendering entirely rather than degrading into a clock that changes nothing —
// see `offerBacked` in MontageFunnel.
//
// Deliberately matched by exact id and never by interval: falling back would
// let a sibling product's yearly price (the account has one at $720) become the
// "full price" we strike through and then charge.
const ANNUAL_FULL_PRICE_ID = "price_1U0fGNE0awkWkU8cb2EGz619";
// Same rule for the three-month tier: exact id only, so the funnel shows it
// solely once it genuinely exists.
const QUARTERLY_PRICE_ID = "";

export const selectFunnelPrices = (products: StripePriceRow[]): FunnelPrices => {
  const rows = Array.isArray(products) ? products : [];
  const ours = rows.filter((p) => p.name === TOOLS_PRODUCT_NAME);

  const pick = (priceId: string, interval: string): FunnelPrice | undefined => {
    const match =
      rows.find((p) => p.price === priceId) ||
      ours.find((p) => p.interval === interval);
    return match ? { priceId: match.price, amount: match.amount } : undefined;
  };

  // No interval fallback for these two. The plans they describe don't exist in
  // Stripe yet, and falling back by interval is exactly how a sibling product's
  // yearly price would end up presented as our struck-through "full price" or
  // sold as our three-month tier.
  const pickExact = (priceId: string): FunnelPrice | undefined => {
    if (!priceId) return undefined;
    const match = rows.find((p) => p.price === priceId);
    return match ? { priceId: match.price, amount: match.amount } : undefined;
  };

  return {
    weekly: pick(WEEKLY_PRICE_ID, "week"),
    annual: pick(ANNUAL_PRICE_ID, "year"),
    annualFull: pickExact(ANNUAL_FULL_PRICE_ID),
    quarterly: pickExact(QUARTERLY_PRICE_ID),
  };
};
